import React from 'react';
import {
  TableRow, TableCell, Box, Avatar, Typography, Chip, TextField, MenuItem,
  IconButton, Tooltip, Badge, Button, Stack
} from '@mui/material';
import {
  Phone as PhoneIcon, Edit as EditIcon, Delete as DeleteIcon,
  AutoAwesome as SparkleIcon, WarningAmber as WarningIcon
} from '@mui/icons-material';
import moment from 'moment';
import { STATUS_COLORS } from './utils';

const FlatLeadRow = ({
  lead,
  customerLeadCount = 1,
  hasSplitAssignee = false,
  userRole,
  users,
  onEdit,
  onDelete,
  onCallHistory,
  onGenerateQuote,
  onQuickAssign,
  onViewQuotations,
  onChangeStatus,
  onSwitchToGrouped
}) => {
  const isMultiLead = customerLeadCount > 1;

  const salesUsers = React.useMemo(() => {
    return users.filter(u => ['Sales Executive', 'Sales Team Lead', 'Sales Manager'].includes(u.permission));
  }, [users]);

  return (
    <TableRow hover sx={{ '&:hover': { bgcolor: '#f0f7ff' }, transition: 'background 0.2s' }}>
      {/* 1. Guest Profile */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: isMultiLead ? '#e0e7ff' : '#e3f2fd',
              color: isMultiLead ? '#4338ca' : '#1565c0',
              fontWeight: 700,
              fontSize: 14,
              border: isMultiLead ? '1.5px solid #818cf8' : 'none'
            }}
          >
            {lead.guest_name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600} color="#1e293b">
                {lead.guest_name}
              </Typography>
              {isMultiLead && (
                <Tooltip title={`This customer has ${customerLeadCount} total inquiries. Click to switch to Grouped Customer View.`}>
                  <Chip
                    size="small"
                    icon={<SparkleIcon sx={{ fontSize: '12px !important' }} />}
                    label={`${customerLeadCount} Inquiries`}
                    onClick={onSwitchToGrouped}
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: '#ede9fe',
                      color: '#6d28d9',
                      border: '1px solid #ddd6fe',
                      '& .MuiChip-icon': { color: '#6d28d9 !important' }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {lead.guest_email || '—'}
            </Typography>
          </Box>
        </Box>
      </TableCell>

      {/* 2. Phone */}
      <TableCell>
        <Typography variant="body2">{lead.guest_phone}</Typography>
      </TableCell>

      {/* 3. Destination / Pax */}
      <TableCell>
        <Typography variant="body2" fontWeight={500}>{lead.destination || 'N/A'}</Typography>
        {lead.pax > 0 && <Typography variant="caption" color="text.secondary">{lead.pax} Pax{lead.kids_above_5 > 0 ? ` + ${lead.kids_above_5} Kids` : ''}</Typography>}
      </TableCell>

      {/* 4. Status */}
      <TableCell>
        <TextField
          select
          size="small"
          value={lead.status || 'New'}
          onChange={(e) => onChangeStatus(lead._id, e.target.value)}
          sx={{
            minWidth: 120,
            '& .MuiSelect-select': {
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              color: STATUS_COLORS[lead.status] === 'primary' ? 'primary.main' :
                     STATUS_COLORS[lead.status] === 'warning' ? 'warning.main' :
                     STATUS_COLORS[lead.status] === 'success' ? 'success.main' :
                     STATUS_COLORS[lead.status] === 'error' ? 'error.main' : 'inherit'
            }
          }}
        >
          <MenuItem value="New" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>New</MenuItem>
          <MenuItem value="In Progress" sx={{ color: 'warning.main', fontWeight: 600, fontSize: '0.875rem' }}>In Progress</MenuItem>
          <MenuItem value="Converted" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.875rem' }}>Converted</MenuItem>
          <MenuItem value="Lost" sx={{ color: 'error.main', fontWeight: 600, fontSize: '0.875rem' }}>Lost</MenuItem>
        </TextField>
      </TableCell>

      {/* 5. Assigned To */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {['Admin', 'Admin-a', 'Sales Manager'].includes(userRole) ? (
            <TextField
              select size="small"
              SelectProps={{ multiple: true }}
              value={Array.isArray(lead.assigned_to) ? lead.assigned_to.map(u => u._id || u.id || u) : []}
              onChange={(e) => onQuickAssign(lead._id, e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {salesUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
          ) : (
            Array.isArray(lead.assigned_to) && lead.assigned_to.length > 0
              ? <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {lead.assigned_to.map(u => (
                    <Chip key={u._id || u.id || u} avatar={<Avatar>{u.fullName?.charAt(0)}</Avatar>} label={u.fullName} size="small" variant="outlined" />
                  ))}
                </Stack>
              : <Typography variant="caption" color="text.secondary">—</Typography>
          )}

          {hasSplitAssignee && (
            <Tooltip title="Warning: Other inquiries of this customer are assigned to different agents!">
              <WarningIcon fontSize="small" sx={{ color: '#d97706' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      {/* 6. Date Added */}
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {lead.createdAt ? moment(lead.createdAt).format('DD MMM YYYY') : '—'}
        </Typography>
      </TableCell>

      {/* 7. Quotes */}
      <TableCell align="center">
        <Badge badgeContent={lead.quotationsCount || 0} showZero color="primary" sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}>
          <Button size="small" variant="outlined" onClick={() => onGenerateQuote(lead)}>Generate</Button>
        </Badge>
        {(lead.quotationsCount > 0) && (
          <Button size="small" variant="text" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }} onClick={() => onViewQuotations(lead)}>
            View All
          </Button>
        )}
      </TableCell>

      {/* 8. Actions */}
      <TableCell align="center">
        <Tooltip title="Call History">
          <IconButton color="info" size="small" onClick={() => onCallHistory(lead)}>
            <Badge
              badgeContent={lead.call_history?.length || 0}
              showZero={false}
              color="info"
              sx={{ '& .MuiBadge-badge': { right: -3, top: -3 } }}
            >
              <PhoneIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton color="primary" size="small" onClick={() => onEdit(lead)}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" size="small" onClick={() => onDelete(lead._id)}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(FlatLeadRow);
